import { Patient } from 'api/models';
import { Identifier, SupportedIdentifiers } from 'types/types';

export function getIsDeadValue(isDead?: boolean | null) {
  if (isDead === null) {
    return null;
  }

  return isDead ? 'yes' : 'no';
}

function getUTCDateMidnight(dateString: string | undefined) {
  if (!dateString) {
    return undefined;
  }
  // Parse the input string into a Date object
  const date = new Date(dateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date string provided');
  }

  // add 1 day to the date
  date.setDate(date.getDate() + 1);

  // Set the time to midnight UTC
  date.setUTCHours(0, 0, 0, 0);

  // Return the UTC midnight date as an ISO string
  return date.toISOString();
}

export function getPatientBody(patient: Patient, id: string): Patient {
  const body = {
    addressCity: patient.addressCity,
    addressHouseNumber: patient.addressHouseNumber,
    addressPostcode: patient.addressPostcode,
    addressStreet: patient.addressStreet,
    birthDate: getUTCDateMidnight(patient.birthDate),
    deathDate: getUTCDateMidnight(patient.deathDate),
    birthFirstName: patient.birthFirstName,
    birthLastName: patient.birthLastName,
    email: patient.email,
    firstName: patient.firstName,
    gender: patient.gender,
    lastName: patient.lastName,
    motherFirstName: patient.motherFirstName,
    motherLastName: patient.motherLastName,
    notes: patient.notes,
    patientProjectId: patient.patientProjectId,
    phoneNumber: patient.phoneNumber,
    titles: patient.titles,
    isDead: patient.isDead,
    identifiers: patient.identifiers,
    patientSubProjectId: patient.patientSubProjectId,
    id: id,
  } as Patient;

  const filteredIds =
    patient.identifiers?.filter(
      (identifier: Identifier) => identifier.type !== SupportedIdentifiers.none,
    ) ?? [];

  if (filteredIds.length > 0) {
    body.identifiers = filteredIds;
  } else {
    body.identifiers = undefined;
  }

  // filter out empty values
  const bodyWithoutEmptyValues = Object.keys(body).reduce<Patient>(
    (acc, key) => {
      const patientKey = key as keyof Patient;
      if (
        body[patientKey] !== undefined &&
        body[patientKey] !== '' &&
        body[patientKey] !== null
      ) {
        // @ts-ignore
        acc[key as keyof Patient] = body[patientKey];
      }
      return acc;
    },
    {} as Patient,
  );

  return bodyWithoutEmptyValues;
}

export function getIsPatientProjectIdDisabled(
  isReadOnly: boolean,
  isProjectIdEditable?: boolean,
) {
  return isReadOnly || !isProjectIdEditable;
}
